import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Circle, CheckCircle2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { StaffSubmissions } from "@/components/common/TaskSubmissions";

// Program task roadmap: CURRENT (active work) · UPCOMING (not yet actionable, unassigned) · COMPLETED.
export function ProgramTaskRoadmap({ enrollmentId, canManage }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get(`/program-tasks/enrollments/${enrollmentId}/roadmap`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [enrollmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activate = async (id) => {
    try { await api.post(`/program-tasks/planned/${id}/activate`); toast.success("Task is now active"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!data || data.items.length === 0) return null;
  const c = data.counts;
  const upcoming = data.items.filter((i) => i.state === "upcoming");
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="program-task-roadmap">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="ptr-toggle">
        <div>
          <p className="text-sm font-semibold text-slate-800">Program Tasks</p>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="ptr-counts">
            Candidate Tasks: {c.candidate.current} current · {c.candidate.upcoming} upcoming · {c.candidate.completed} completed
            <span className="mx-2 text-slate-300">|</span>
            District Support Tasks: {c.staff.current} current · {c.staff.upcoming} upcoming · {c.staff.completed} completed
          </p>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {["current", "upcoming", "completed"].map((s) => {
            const rows = data.items.filter((i) => i.state === s);
            if (!rows.length) return null;
            return (
              <div key={s} data-testid={`ptr-section-${s}`}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{s}</p>
                <div className="divide-y rounded-lg border border-slate-100">
                  {rows.map((i) => (
                    <div key={i.id} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`ptr-item-${i.id}`}>
                      {s === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className={`w-4 h-4 ${s === "current" ? "text-brand" : "text-slate-300"}`} />}
                      <span className="flex-1 text-slate-700">{i.title}{i.status === "submitted" ? " · Submitted" : i.status === "needs_attention" ? " · Needs Attention" : ""}</span>
                      <span className="text-[11px] text-slate-400">{i.is_candidate ? "Candidate" : "District Support"}{i.phase && s === "upcoming" ? ` · ${i.phase}` : ""}</span>
                      {s === "upcoming" && canManage && data.can_activate && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-brand" onClick={() => activate(i.id)} data-testid={`ptr-activate-${i.id}`}>
                          <PlayCircle className="w-3.5 h-3.5 mr-1" />Activate Task Now
                        </Button>
                      )}
                    </div>
                  ))}
                  {s === "current" && rows.filter((i) => i.is_candidate).map((i) => <StaffSubmissions key={`sub-${i.id}`} taskId={i.id} onChanged={load} />)}
                </div>
              </div>
            );
          })}
          {upcoming.length > 0 && <p className="text-[11px] text-slate-400">Upcoming tasks become active — and are assigned to the candidate's current recruiter — when the candidate reaches that stage.</p>}
        </div>
      )}
    </div>
  );
}
